'use client'
import { useLayoutEffect, useState, type RefObject } from 'react'

const BOTTOM_GAP_PX = 24
const MIN_HEIGHT_PX = 240

interface LauncherBounds {
  top: number
  maxHeight?: string
}

/**
 * The panel's top and height budget: the viewport below the bar less a gap, measured
 * from where the panel actually sits (the disclosure bar above the nav pushes
 * it down while the page is at the top of its scroll). Re-measured on resize;
 * the page cannot scroll while the launcher is open, so nothing else moves it.
 */
export const useLauncherBounds = (anchorRef: RefObject<HTMLElement | null>, present: boolean) => {
  const [bounds, setBounds] = useState<LauncherBounds>({ top: 80 })

  useLayoutEffect(() => {
    if (!present) {
      return undefined
    }
    const measure = () => {
      const top = anchorRef.current?.getBoundingClientRect().top ?? 80
      setBounds({
        top: Math.max(0, top),
        maxHeight: `${Math.max(MIN_HEIGHT_PX, Math.round(window.innerHeight - top - BOTTOM_GAP_PX))}px`,
      })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [anchorRef, present])

  return bounds
}
